/**
 * Email OTP Authentication Hooks
 *
 * TanStack Query v5 hooks for email OTP-based authentication.
 * Primary auth method for the unified AmalQ authentication system.
 *
 * @module features/auth/hooks/useEmailOtp
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query';

import {
  sendEmailOtp,
  verifyEmailOtp,
  registerOtpUser,
  type SendEmailOtpRequest,
  type SendEmailOtpSuccessResponse,
  type VerifyEmailOtpRequest,
  type VerifyEmailOtpSuccessResponse,
  type OtpRegisterRequest,
  type OtpRegisterSuccessResponse,
} from '../services/userAuthApi';
import { authKeys } from './queryKeys';
import { isCaptchaChallenge } from '@lib/captcha';

// ─────────────────────────────────────────────────────────────────────────────
// Hook Return Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SendOtpResult {
  email: string;
  expiresInSeconds: number;
  isNewUser: boolean;
  /** Job ID for tracking email validation status via SSE (only for unknown domains) */
  validationJobId?: string;
  /** True if OTP was actually sent (expiresIn > 0). False means collect name first. */
  otpSent: boolean;
}

export interface VerifyOtpResult {
  user: VerifyEmailOtpSuccessResponse['data']['user'];
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
  isNewUser: boolean;
}

export interface OtpRegisterResult {
  userId: string;
  email: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Send OTP Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Send OTP code to user's email
 *
 * Initiates the OTP authentication flow by sending a 6-digit code.
 * Works for both login (existing users) and registration (new users).
 *
 * @example
 * ```tsx
 * const { mutate: sendOtp, isPending } = useSendOtp();
 *
 * const handleSendOtp = (email: string) => {
 *   sendOtp({ email }, {
 *     onSuccess: (result) => {
 *       console.log(`OTP sent to ${result.email}`);
 *       console.log(`Is new user: ${result.isNewUser}`);
 *       // Navigate to OTP verification screen
 *     },
 *     onError: (error) => {
 *       // Handle error (could be CAPTCHA challenge)
 *     },
 *   });
 * };
 * ```
 */
export function useSendOtp(
  options?: Omit<
    UseMutationOptions<SendOtpResult, Error, SendEmailOtpRequest>,
    'mutationFn'
  >
) {
  return useMutation({
    mutationFn: async (request: SendEmailOtpRequest): Promise<SendOtpResult> => {
      let response;

      try {
        response = await sendEmailOtp(request);
      } catch (error) {
        // Network error or unexpected failure
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Network error. Please check your connection and try again.');
      }

      // Check for error response (e.g., rate limiting, validation errors)
      // Error responses have { success: false, error: "..." } structure
      const responseAny = response as unknown as { success?: boolean; error?: string; message?: string };
      if (responseAny.success === false) {
        const errorMessage = responseAny.error || responseAny.message || 'Failed to send verification code';
        throw new Error(errorMessage);
      }

      // Check for CAPTCHA challenge
      if (isCaptchaChallenge(response)) {
        throw new Error('CAPTCHA_REQUIRED');
      }

      // Type narrowing: at this point response is SendEmailOtpSuccessResponse
      const successResponse = response as SendEmailOtpSuccessResponse;

      // Validate that we have the expected data structure
      if (!successResponse.data || typeof successResponse.data.isNewUser !== 'boolean') {
        throw new Error('Invalid response from server. Please try again.');
      }

      // Return the result - use request.email since API returns maskedEmail
      // otpSent is true when expiresIn > 0 (OTP was actually sent to email)
      return {
        email: request.email,
        expiresInSeconds: successResponse.data.expiresIn,
        isNewUser: successResponse.data.isNewUser,
        validationJobId: successResponse.data.validationJobId,
        otpSent: successResponse.data.expiresIn > 0,
      };
    },
    ...options,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Verify OTP Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Verify OTP code and complete authentication
 *
 * Verifies the 6-digit code entered by the user.
 * On success:
 * - Stores JWT tokens in localStorage
 * - Invalidates auth-related queries
 * - Returns user data
 *
 * @example
 * ```tsx
 * const { mutate: verifyOtp, isPending } = useVerifyOtp({
 *   onSuccess: (result) => {
 *     // Auth completed - user is logged in
 *     console.log(`Welcome ${result.user.name || result.user.email}`);
 *     if (result.isNewUser) {
 *       // Redirect to onboarding
 *     } else {
 *       // Redirect to dashboard
 *     }
 *   },
 * });
 *
 * const handleVerify = (email: string, code: string) => {
 *   verifyOtp({ email, code });
 * };
 * ```
 */
export function useVerifyOtp(
  options?: Omit<
    UseMutationOptions<VerifyOtpResult, Error, VerifyEmailOtpRequest>,
    'mutationFn'
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: VerifyEmailOtpRequest): Promise<VerifyOtpResult> => {
      const response = await verifyEmailOtp(request);

      // Check for error response (e.g., invalid code, expired code)
      if (!response.success) {
        const errorResponse = response as unknown as { error?: string; message?: string };
        const errorMessage = errorResponse.error || errorResponse.message || 'OTP verification failed';
        throw new Error(errorMessage);
      }

      // Type narrowing: at this point response is VerifyEmailOtpSuccessResponse
      const successResponse = response as VerifyEmailOtpSuccessResponse;

      // Return full data - let caller handle auth state via completeAuthentication
      // This follows single responsibility: hook handles API, caller handles auth state
      return {
        user: successResponse.data.user,
        tokens: {
          accessToken: successResponse.data.tokens.accessToken,
          refreshToken: successResponse.data.tokens.refreshToken,
          expiresIn: successResponse.data.tokens.expiresIn,
        },
        isNewUser: successResponse.data.isNewUser,
      };
    },
    onSuccess: () => {
      // Invalidate all auth-related queries to refetch with new auth state
      queryClient.invalidateQueries({
        queryKey: authKeys.all(),
      });
    },
    ...options,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// OTP Registration Hook (New Users)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Register a new user via OTP flow
 *
 * Called when `isNewUser: true` is returned from `useSendOtp`.
 * Collects the user's name and creates their account before OTP verification.
 *
 * Flow:
 * 1. User enters email → sendOtp returns { isNewUser: true }
 * 2. UI shows name collection form
 * 3. User submits name → this hook creates account
 * 4. UI shows OTP verification (same code already sent)
 *
 * @example
 * ```tsx
 * const { mutate: registerUser, isPending } = useOtpRegister({
 *   onSuccess: (result) => {
 *     console.log(`Account created for ${result.email}`);
 *     // Now show OTP verification step
 *   },
 * });
 *
 * const handleRegister = (email: string, firstName: string, lastName: string) => {
 *   registerUser({ email, firstName, lastName });
 * };
 * ```
 */
export function useOtpRegister(
  options?: Omit<
    UseMutationOptions<OtpRegisterResult, Error, OtpRegisterRequest>,
    'mutationFn'
  >
) {
  return useMutation({
    mutationFn: async (request: OtpRegisterRequest): Promise<OtpRegisterResult> => {
      let response;

      try {
        response = await registerOtpUser(request);
      } catch (error) {
        // Network error or unexpected failure
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('Network error. Please check your connection and try again.');
      }

      // Check for error response
      const responseAny = response as unknown as { success?: boolean; error?: string; message?: string };
      if (responseAny.success === false) {
        const errorMessage = responseAny.error || responseAny.message || 'Failed to create account';
        throw new Error(errorMessage);
      }

      // Type narrowing: at this point response is OtpRegisterSuccessResponse
      const successResponse = response as OtpRegisterSuccessResponse;

      // Validate that we have the expected data structure
      if (!successResponse.data?.user?.id) {
        throw new Error('Invalid response from server. Please try again.');
      }

      return {
        userId: successResponse.data.user.id,
        email: successResponse.data.user.email,
      };
    },
    ...options,
  });
}
